import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ContractorService from "../../services/ContractorService";
import MasterLocationService from "../../services/MasterLocationService";
import Breadcrumb from "../../components/Breadcrumb";
import StorageService from "../../services/StorageService";
import { useParams } from "react-router-dom";
import MessageDialog from "../../components/MessageDialog";

export default function AddContractor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  // const messageTimer = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [contractorId, setContractorId] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const { id } = useParams();
  // const messageBoxRef = useRef(null);


  const currentYear = new Date().getFullYear();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const country = watch("countryId");
  const state = watch("state");

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  // const closeMessage = () => {
  //   setMessage("");
  //   if (messageTimer.current) clearTimeout(messageTimer.current);
  // };

  // useEffect(() => {
  //   if (message && messageBoxRef.current) {
  //     const yOffset = -120; //adjust this value if needed
  //     const y =
  //       messageBoxRef.current.getBoundingClientRect().top +
  //       window.pageYOffset +
  //       yOffset;

  //     window.scrollTo({
  //       top: y,
  //       behavior: "smooth",
  //     });
  //   }
  // }, [message]);



  // Load countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await MasterLocationService.getAllCountries();
        setCountries(data || []);
      } catch (err) {
        console.error("Error fetching countries:", err);
        setCountries([]);
      }
    };
    fetchCountries();
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (country) {
      const fetchStates = async () => {
        try {
          const data = await MasterLocationService.getStatesByCountry(country);
          setStates(data || []);

          // Reset state & city only if adding new contractor
          if (!editMode) {
            setValue("state", "");
            setCities([]);
            setValue("city", "");
          }
        } catch (err) {
          console.error("Error fetching states:", err);
          setStates([]);
          setCities([]);
          setValue("state", "");
          setValue("city", "");
        }
      };
      fetchStates();
    } else {
      setStates([]);
      setCities([]);
      setValue("state", "");
      setValue("city", "");
    }
  }, [country, setValue, editMode]);

  // Load cities when state changes
  useEffect(() => {
    if (state) {
      const fetchCities = async () => {
        try {
          const data = await MasterLocationService.getCitiesByState(state);
          setCities(data || []);

          if (!editMode) setValue("city", "");
        } catch (err) {
          console.error("Error fetching cities:", err);
          setCities([]);
          if (!editMode) setValue("city", "");
        }
      };
      fetchCities();
    } else {
      setCities([]);
      if (!editMode) setValue("city", "");
    }
  }, [state, setValue, editMode]);

  useEffect(() => {
    const loadContractor = async () => {
      if (!id) {
        setEditMode(false);
        reset({ status: "Active" });
        return;
      }

      setEditMode(true);
      setContractorId(id);
      setEditLoading(true);

      try {
        //  Fetch contractor details from DB
        const record = await ContractorService.getById(id);

        if (!record) {
          showMessage("Unable to load contractor details", "error");
          return;
        }

        //  Fill non dependent fields
        const simpleFields = { ...record };
        delete simpleFields.countryId;
        delete simpleFields.state;
        delete simpleFields.city;

        Object.entries(simpleFields).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            setValue(key, value);
          }
        });

        //  Load countries
        const countryData = await MasterLocationService.getAllCountries();
        setCountries(countryData);
        setValue("countryId", record.countryId);

        //  Load states for the selected country
        if (record.countryId) {
          const stateData = await MasterLocationService.getStatesByCountry(record.countryId);
          setStates(stateData);
          setValue("state", record.state);
        }

        //  Load cities for selected state
        if (record.state) {
          const cityData = await MasterLocationService.getCitiesByState(record.state);
          setCities(cityData);
          setValue("city", record.city);
        }
      } catch (error) {
        console.error(error);
        showMessage("Error loading contractor details", "error");
      }
      finally {
        setEditLoading(false);
      }
    };

    loadContractor();
  }, [id, setValue, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      let res;

      if (editMode && contractorId) {
        data.updatedBy = StorageService.loginData().employeeId;
        res = await ContractorService.update(contractorId, data);
      } else {
        data.createdBy = StorageService.loginData().employeeId;
        data.companyId = StorageService.activeCompany().companyId;
        res = await ContractorService.create({ ...data, status: "Active" });
      }

      console.log("Save Response:", res);

      //  Success Response
      if (res?.status === true) {
        showMessage(
          editMode
            ? "Contractor updated successfully."
            : "Contractor added successfully.",
          "success"
        );
        setTimeout(() => {
          navigate("/cpnl/contractors/view");
        }, 1000);
      }
      //  Duplicate Handling
      else if (
        res?.message &&
        res.message.toLowerCase().includes("duplicate")
      ) {
        showMessage("Duplicate Contractor Name or Code!", "error");
      }
      //  Other Failures
      else {
        showMessage("Failed to save Contractor.", "error");
      }
    } catch (error) {
      console.error("Error saving contractor:", error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Error saving contractor";
      //  Catch duplicate from backend error message too
      if (msg.toLowerCase().includes("duplicate")) {
        showMessage("Duplicate Contractor Name or Code!", "error");
      } else {
        showMessage(msg, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/cpnl/contractors/view");
  };

  return (
    <>
      <Breadcrumb
        pageTitle={editMode ? "Modify Contractor" : "Add Contractor"}
        secondTitle="Masters"
      ></Breadcrumb>
      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          {/* {message && (
            <div
              // ref={messageBoxRef}
              className={`alert ${msgType === "success" ? "alert-success" : "alert-danger"
                } d-flex justify-content-between align-items-center w-50 mb-3 p-2`}
            >
              <span>{message}</span>
              <button
                type="button"
                className="btn-close"
                onClick={closeMessage}
              ></button>
            </div>
          )} */}

          
            <MessageDialog
              message={message}
              type={msgType}
              setMessage={setMessage}
              setMsgType={setMsgType}
            />
          
          {editLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="mt-2 text-muted">Loading data...</div>
            </div>
          ) : (

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="row">
                {/* Contractor Name */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Contractor Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("contractorName", {
                      required: "Contractor Name is required",
                      minLength: { value: 3, message: "At least 3 characters" },
                    })}
                  />
                  {errors.contractorName && (
                    <small className="text-danger">
                      {errors.contractorName.message}
                    </small>
                  )}
                </div>

                {/* Contractor Code */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Contractor Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("contractorCode", {
                      required: "Contractor Code is required",
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.contractorCode && (
                    <small className="text-danger">
                      {errors.contractorCode.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="row">
                {/* First Name */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Contact First Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("firstName")}
                  />
                  {errors.firstName && (
                    <small className="text-danger">
                      {errors.firstName.message}
                    </small>
                  )}
                </div>

                {/* Last Name */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Contact Last Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("lastName")}
                  />
                  {errors.lastName && (
                    <small className="text-danger">{errors.lastName.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                {/* Email */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Email ID
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    {...register("emailId", {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email",
                      },
                    })}
                  />
                  {errors.emailId && (
                    <small className="text-danger">{errors.emailId.message}</small>
                  )}
                </div>

                {/* Mobile No */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Mobile No
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("mobileNo", {
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Enter valid 10-digit number",
                      },
                    })}
                  />
                  {errors.mobileNo && (
                    <small className="text-danger">{errors.mobileNo.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                {/* Phone No */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">Phone No</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("phone")}
                  />
                </div>

                {/* Fax */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">Fax</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("fax")}
                  />
                </div>
              </div>

              <div className="row">
                {/* Address */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("address")}
                  />
                </div>

                {/* Country */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Country <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("countryId", { required: "Country is required" })}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.countryName}
                      </option>
                    ))}
                  </select>
                  {errors.country && (
                    <small className="text-danger">{errors.country.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                {/* State */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    State <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("state", { required: "State is required" })}
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.stateName}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <small className="text-danger">{errors.state.message}</small>
                  )}
                </div>

                {/* District */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    District <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("city", { required: "District is required" })}
                  >
                    <option value="">Select District</option>
                    {cities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.cityName}
                      </option>
                    ))}
                  </select>
                  {errors.city && (
                    <small className="text-danger">{errors.city.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                {/* Zip */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">Zip</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("zip")}
                  />
                </div>

                {/* Establishment Year */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Establishment Year
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("estableshmentYear", {
                      valueAsNumber: true,
                      validate: (v) => {

                        if (v < 1900 || v > currentYear)
                          return `Enter year between 1900 and ${currentYear}`;
                        return true;
                      },
                    })}
                  />
                  {errors.estableshmentYear && (
                    <small className="text-danger">
                      {errors.estableshmentYear.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="row">
                {/* PAN */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    PAN
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("PAN", {
                      pattern: {
                        value: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
                        message: "PAN must be 10 characters (e.g. AAAAA9999A)",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.PAN && (
                    <small className="text-danger">{errors.PAN.message}</small>
                  )}
                </div>

                {/* TAN */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">TAN</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("TAN", {
                      pattern: {
                        value: /^[A-Z]{4}[0-9]{5}[A-Z]$/,
                        message: "TAN must be 10 characters (e.g. AAAA99999A)",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.TAN && (
                    <small className="text-danger">{errors.TAN.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                {/* GSTN */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    GSTN
                    {/* <span className="text-danger">*</span> */}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("GSTN", {
                      pattern: {
                        value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
                        message: "GSTN must be 15 characters",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.GSTN && (
                    <small className="text-danger">{errors.GSTN.message}</small>
                  )}
                </div>

                {/* CIN */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">CIN</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("CIN", {
                      minLength: {
                        value: 21,
                        message: "CIN must be 21 characters",
                      },
                      maxLength: {
                        value: 21,
                        message: "CIN must be 21 characters",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.CIN && (
                    <small className="text-danger">{errors.CIN.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                {/* Trademark No */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">Trademark No</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("trademarkNo")}
                  />
                </div>

                {/* {editMode && (
                <div className="mb-3 col-md-6">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    {...register("status", { required: true })}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              )} */}
              </div>

              <div className="mt-3">
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={loading}
                >
                  <i class="ti ti-circle-check me-1"></i>
                  {loading
                    ? editMode
                      ? "Updating..."
                      : "Saving..."
                    : editMode
                      ? "Update"
                      : "Save"}
                </button>
                <button
                  type="button"
                  className="d-inline-flex btn btn-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <i class="ti ti-cancel me-1"></i> Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
